<script setup>
import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import { computed } from "vue";
import { launchSupportBeacon } from "@/Composables/launchSupportBeacon.js";

const props = defineProps({
    beaconId: String,
    status: Number,
});

const page = {
    props: {
        beaconId: props.beaconId,
    },
};

const title = computed(() => {
    return {
        503: "Service Unavailable",
        500: "Server Error",
        405: "Method Not Allowed",
        404: "Page Not Found",
        403: "Forbidden",
        401: "Unauthorized",
    }[props.status];
});

const description = computed(() => {
    return {
        503: "Sorry, we are doing some maintenance. Please check back soon.",
        500: "Whoops, something went wrong on our servers.",
        405: "Sorry, the page you are looking for could not be found.",
        404: "Sorry, the page you are looking for could not be found.",
        403: "Sorry, you are not authorized to access this page.",
        401: "Sorry, you are not authorized to access this page.",
    }[props.status];
});
</script>

<template>
    <div
        class="grid min-h-screen grid-cols-1 grid-rows-[1fr,auto,1fr] bg-white dark:bg-midnight lg:grid-cols-[max(50%,36rem),1fr]"
    >
        <header
            class="mx-auto w-full max-w-7xl px-6 pt-6 sm:pt-10 lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:px-8"
        >
            <a href="/">
                <span class="sr-only">Vamp</span>
                <ApplicationLogo
                    class="h-10 w-auto sm:h-12 text-primary dark:text-brand"
                />
            </a>
        </header>
        <main
            class="mx-auto w-full max-w-7xl px-6 py-24 sm:py-32 lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:px-8"
        >
            <div class="max-w-lg">
                <p
                    class="text-base font-semibold leading-8 text-primary-dark dark:text-primary"
                >
                    {{ status }}
                </p>
                <h1
                    class="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-300 sm:text-5xl"
                >
                    {{ title }}
                </h1>
                <p
                    class="mt-6 text-base leading-7 text-gray-600 dark:text-gray-400"
                >
                    {{ description }}
                </p>
                <div class="mt-10">
                    <a
                        :href="route('home')"
                        class="text-sm font-semibold leading-7 text-primary-dark dark:text-primary"
                        ><span aria-hidden="true">&larr;</span> Back to home</a
                    >
                </div>
            </div>
        </main>
        <footer class="self-end lg:col-span-2 lg:col-start-1 lg:row-start-3">
            <div class="border-t border-gray-100 bg-gray-50 py-10">
                <nav
                    class="mx-auto flex w-full max-w-7xl items-center gap-x-4 px-6 text-sm leading-7 text-gray-600 dark:text-gray-400 lg:px-8"
                >
                    <button @click="launchSupportBeacon(page)">
                        Contact support
                    </button>
                    <svg
                        viewBox="0 0 2 2"
                        aria-hidden="true"
                        class="h-0.5 w-0.5 fill-gray-300"
                    >
                        <circle cx="1" cy="1" r="1" />
                    </svg>
                    <a href="https://support.vamp.bot" target="_blank"
                        >Knowledge Base</a
                    >
                </nav>
            </div>
        </footer>
        <div
            class="hidden lg:relative lg:col-start-2 lg:row-start-1 lg:row-end-4 lg:block"
        >
            <img
                src="https://images.unsplash.com/photo-1470847355775-e0e3c35a9a2c?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1825&q=80"
                alt=""
                class="absolute inset-0 h-full w-full object-cover"
            />
        </div>
    </div>
</template>
